/**
 * A time zone named in plain words with its abbreviation, for the Profile row in the
 * Mini App and on the website: "Eastern Time (EDT)" (Gabriel 2026-10-19, "ok agree").
 *
 * The browser's generic name ("Eastern Time") and short name ("EDT") at `at`; US zones
 * come from a fixed map, since browsers name some of them by their standard time
 * ("Mountain Standard Time" for Phoenix). A zone the browser names only by its offset
 * falls back to its long name, and an unknown zone to its IANA id.
 */

const US_ZONES: Record<string, string> = {
  'America/New_York': 'Eastern Time',
  'America/Detroit': 'Eastern Time',
  'America/Chicago': 'Central Time',
  'America/Denver': 'Mountain Time',
  'America/Phoenix': 'Mountain Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Anchorage': 'Alaska Time',
  'Pacific/Honolulu': 'Hawaii-Aleutian Time',
};

const OFFSET_NAME = /^(GMT|UTC)([+\-−]|$)/;

type ZoneNameStyle = 'long' | 'short' | 'longGeneric';

function zoneName(zone: string, style: ZoneNameStyle, at: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: style }).formatToParts(at);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

export function timeZoneInWords(zone: string, at: Date = new Date()): string {
  const words = (name: string) => (name && !OFFSET_NAME.test(name) ? name : '');
  const name = US_ZONES[zone] || words(zoneName(zone, 'longGeneric', at)) || words(zoneName(zone, 'long', at));
  if (!name) return zone;
  const abbreviation = zoneName(zone, 'short', at);
  return abbreviation && abbreviation !== name ? `${name} (${abbreviation})` : name;
}
